import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import { SessionState, VitalSigns, VitalSignsResults } from '@binah/web-sdk';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';

// import UAParser from 'ua-parser-js';

import useCameras from 'hooks/useCameras';
import useError from 'hooks/useError';
import useMonitor from 'hooks/useMonitor';
import usePageVisibility from 'hooks/usePageVisibility';
import usePrevious from 'hooks/usePrevious';
import {
  getCurrentTimestamp,
  getWebcamResolution,
  isMilestoneCompleted,
} from 'lib/utils';

import useTimer from 'hooks/useTimer';
import useFaceScanStore from 'store/face-scan';
import { captureUserActivity } from 'services/profile/api';
import { USER_ACTIVITY } from '../../../types';
import { patchScanSession, postHealthReading } from 'services/face-scan/api';
import { getBinahKey } from 'lib/store';
import ScanErrorDialog from 'components/dialogs/ScanErrorDialog';
import Loader from 'components/Loader';
import { PATH_ROUTES } from 'routes/path';
import Scan from './Scan';
import { usePostOnboardingStep } from 'services/dashboard/hooks';
import { MILESTONES } from 'lib/constants';
import { formatHealthReading } from 'lib/health-readings';
import { t } from 'i18next';
import { useScanImageCaptureAndUpload } from './useScanImageCapture';
import { scanImageCaptureConfig } from './scan-config';

export type ValidityCount = {
  [key: string]: number;
};

export default function BinahMonitor() {
  const navigate = useNavigate();

  const sdkConfig = useFaceScanStore((store) => store.sdkConfig);
  const processingTime = sdkConfig?.scan_duration || 0;
  const licenseKey = sdkConfig?.binaah_sdk_key || '';

  const cameras = useCameras();
  const [cameraId, setCameraId] = useState<string>();
  // const [isMobile] = useState(
  //   UAParser(navigator.userAgent).device.type === 'mobile'
  // );

  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  useEffect(() => {
    if (!cameras?.length) return;
    setCameraId(cameras[0].deviceId);
  }, [cameras]);

  const videoRef = useRef<HTMLVideoElement>(null);

  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [_, setIsLoading] = useState<boolean>(false);
  const [loadingTimeoutPromise, setLoadingTimeoutPromise] = useState<number>();
  const [__, setIsMeasurementEnabled] = useState<boolean>(false);
  const [finalResults, setFinalResults] = useState<VitalSigns | null>(null);
  const [imageValidityJSON, setImageValidityJSON] = useState<ValidityCount>({});
  const [readingId, setReadingId] = useState<string>('');
  console.log("🚀 ~ BinahMonitor ~ readingId:", readingId)
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [isPostingData, setIsPostingData] = useState<boolean>(false);

  const handleValidityJSON = (validity: string) => {
    setImageValidityJSON((prevCounts) => ({
      ...prevCounts,
      [validity]: (prevCounts[validity] || 0) + 1,
    }));
  };

  function onComplete(results: VitalSignsResults) {
    setFinalResults(results.results);
  }

  const { sessionState, vitalSigns, error, info } = useMonitor(
    videoRef as MutableRefObject<HTMLVideoElement>,
    cameraId!,
    processingTime,
    licenseKey,
    '',
    startMeasuring,
    onComplete,
    handleValidityJSON
  );

  const prevSessionState = usePrevious(sessionState);
  const errorMessage = useError(error);
  // const warningMessage = useWarning(warning);
  const isPageVisible = usePageVisibility();

  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState]
  );

  const durationSeconds = useTimer(isMeasuring(), processingTime);
  useScanImageCaptureAndUpload({
    videoRef,
    isMeasuring: isMeasuring(),
    durationSeconds,
    config: scanImageCaptureConfig,
    readingId,
    resetKey: `${cameraId ?? ''}:${readingId ?? ''}`,
  });

  const { mutateAsync: updateOnboarding } = usePostOnboardingStep();
  const { mutate, data: postHealthRes } = useMutation({
    mutationFn: postHealthReading,
    onSuccess: async (data: any) => {
      if (!data.success) {
        try {
          await captureUserActivity(USER_ACTIVITY.SCAN_ERROR, {
            message: imageValidityJSON,
            reading_id: readingId,
          });
          await patchScanSession({
            session_id: readingId,
            status: USER_ACTIVITY.SCAN_ERROR,
          });
        } catch (error) {
          console.error(error);
        }
        setShowErrorDialog(true);
        setIsPostingData(false);
        return;
      }

      try {
        if (!isMilestoneCompleted(MILESTONES.QUESTIONNAIRE)) {
          await updateOnboarding({ milestone: MILESTONES.QUESTIONNAIRE });
        }
        if (
          !isMilestoneCompleted(MILESTONES.ONBOARDED) &&
          isMilestoneCompleted(MILESTONES.FIRST_HEALTH_MEASUREMENT)
        ) {
          await updateOnboarding({ milestone: MILESTONES.ONBOARDED });
        }

        captureUserActivity(USER_ACTIVITY.END_SCAN, {
          reading_id: readingId,
        });
        await patchScanSession({
          session_id: readingId,
          status: USER_ACTIVITY.END_SCAN,
        });
      } catch (error) {
        console.error(error);
      }
      setIsPostingData(false);
      navigate(PATH_ROUTES.FACE_SCAN_RESULT);
    },
    onError: () => { },
    onSettled: () => setIsPostingData(false),
  });

  const isCompleted = durationSeconds >= processingTime;
  useEffect(() => {
    if (finalResults && isCompleted) {
      setIsPostingData(true);
      mutate({
        data: formatHealthReading(finalResults),
        scan_error: imageValidityJSON,
        timestamp: getCurrentTimestamp(),
        reading_id: readingId,
        binaah_key: getBinahKey(),
      });
    }
  }, [isCompleted, finalResults]);

  // const isVideoReady = useCallback(
  //   () => videoRef.current?.readyState === VideoReadyState.HAVE_ENOUGH_DATA,
  //   []
  // );

  const onMeasure = useCallback(async () => {
    setIsLoading(true);
    if (sessionState === SessionState.ACTIVE) {
      const newSessionId = uuidv4();

      try {
        await patchScanSession({
          session_id: newSessionId,
          status: USER_ACTIVITY.START_SCAN,
        });
        captureUserActivity(USER_ACTIVITY.START_SCAN, {
          reading_id: newSessionId,
        });
      } catch (error) {
        console.error(error);
        return;
      }

      // getting camera resolution of webcam
      const { resolution, error } = await getWebcamResolution();
      if (!isMediaMobile && !error) {
        toast(
          () => (
            <span className='text-sm font-isidora'>
              {t('webcam_resolution_msg')}{' '}
              <span className='font-semibold'>{resolution.toFixed(2)}MP</span>
            </span>
          ),
          { position: 'top-right' }
        );
      }

      setStartMeasuring(true);
      setReadingId(newSessionId);
      setLoadingTimeoutPromise(
        window.setTimeout(() => setIsLoading(true), processingTime * 1000)
      );
    } else if (isMeasuring()) {
      try {
        captureUserActivity(USER_ACTIVITY.STOP_SCAN, {
          message: 'restart session',
          reading_id: readingId,
        });
        await patchScanSession({
          session_id: readingId,
          status: USER_ACTIVITY.STOP_SCAN,
        });
      } catch (error) {
        console.error(error);
      }

      clearTimeout(loadingTimeoutPromise);
      setStartMeasuring(false);
      setReadingId('');
    }
  }, [sessionState, setIsLoading, processingTime]);

  useEffect(() => {
    if (isMeasuring()) {
      setIsLoading(false);
      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage
    ) {
      setIsMeasurementEnabled(false);
    }
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
  }, [errorMessage, sessionState, isPageVisible]);

  useEffect(() => {
    // return () => navigate(0);
  }, []);

  if (isPostingData) return <Loader />;

  const onErrorClose = () => {
    setShowErrorDialog(false);
    navigate(0);
    navigate(`${PATH_ROUTES.FACE_SCAN}?showScan=true`);
  };

  const onRescan = () => {
    setShowErrorDialog(false);
    onMeasure();
  };

  return (
    <>
      <Scan
        info={info}
        isMeasuring={isMeasuring}
        onMeasure={onMeasure}
        videoRef={videoRef}
        vitalSigns={vitalSigns}
      />

      <ScanErrorDialog
        open={showErrorDialog}
        onClose={onErrorClose}
        onRescan={onRescan}
        error={postHealthRes}
      />
    </>
  );
}
